/* eslint-disable react-hooks/exhaustive-deps */
import { FunctionComponent as F, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import PageHeadline from '../../components/UI/Text/PageHeadline';
import { Box, Typography, Container, Stack, FilterOptionsState, Select, MenuItem } from '@mui/material';
import { getMmProtocols } from '../../core/http/mmProtocol';
import { IOption } from '@protoplan/types';
import { IState } from '../../core/store/store';
import { stringSimilarity } from "string-similarity-js";
import ControlledAutocomplete from '../../components/UI/Input/ControlledAutocomplete';
import Button from '../../components/UI/Button/Button';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import { validateDose } from '../../utils/validate';
import { PROTOCOL } from '../../core/router/paths';
import { combineProtocolsAsync } from '../../core/store/protocol/protocol.thunks';
import { useAppDispatch } from '../../core/store/hooks';
import ReplaceProtocolDialog from './ReplaceProtocolDialog';


interface IProtocolOption extends IOption
{
  protocolId?: number,
  mmProtocolId?: number,
}

const ProtocolBuilder: F = () =>
{
  const navigate = useNavigate();
  const appDispatch = useAppDispatch();

  const [searchParams, setSearchParams] = useSearchParams();

  const [showSearch, setShowSearch] = useState(!searchParams.getAll("protocols").length);
  const [mmProtocolOptions, setMmProtocolOptions] = useState<IProtocolOption[]>([]);

  const user = useSelector((state: IState) => state.user);
  const userProtocols = useSelector((state: IState) => state.protocol.protocols);
  const userProtocolOptions = userProtocols
    .map(p => ({ protocolId: p.protocolId, label: p.name, value: p.protocolId })) as IProtocolOption[];

  const availableOptions = (all: IProtocolOption[]) => all
    .filter(o => searchParams.getAll("protocols").every(p => !p.includes(String(Math.abs(o.value)))));
  const [options, setOptions] = useState<IProtocolOption[]>(availableOptions(userProtocolOptions));
  const [currentOption, setCurrentOption] = useState<IProtocolOption | null>(null);
  const [replaceProtocolDialog, setReplaceProtocolDialog] = useState(false);

  // Set selected protocols from URL params
  const selectedProtocols = searchParams.getAll("protocols")
    .map(param => ({
      protocol: (param.includes("mm") ?
        mmProtocolOptions.find(o => o.mmProtocolId === Number(param.split("_")[1])) :
        userProtocolOptions.find(o => o.protocolId === Number(param.split("_")[1]))),
      weighting: Number(param.split("_")[2]),
      weightingError: !validateDose(param.split("_")[2]) }))
    .filter(p => p.protocol) as { protocol: IProtocolOption, weighting: number, weightingError: boolean }[];

  // Retrieve MM protocols from server
  useEffect(() =>
  {
    getMmProtocols()
      .then(mmProtocols =>
      {
        if(!mmProtocols)
          return;

        const retrievedMmProtocolOptions = mmProtocols?.map(p =>
          ({ mmProtocolId: p.mmProtocolId, label: p.name + " - " + p.bookTitle, value: -p.mmProtocolId }));

        setMmProtocolOptions(retrievedMmProtocolOptions);

        setOptions(availableOptions(userProtocolOptions.concat(retrievedMmProtocolOptions)));
      })
  }, []);



  const similar = (fullStr: string, partialStr: string) =>
    (stringSimilarity(fullStr, partialStr) * fullStr.length / partialStr.length) > 0.8

  async function handleInputChange(_event: React.SyntheticEvent, value: string)
  {
    if(!value)
      return;

    setOptions(availableOptions(userProtocolOptions.filter(o => similar(o.label, value))
      .concat(mmProtocolOptions.filter(o => similar(o.label, value)))
      .sort((a, b) =>
        stringSimilarity(b.label, value) - (b.label.includes(value) ? 1 : 0) -
        stringSimilarity(a.label, value) + (a.label.includes(value) ? 1 : 0))));
  }

  async function handleChangeCurrentOption(option: IProtocolOption | null)
  {
    setCurrentOption(option);
  }

  function optionsFilter<T>(opts: (IOption & T)[], params?: FilterOptionsState<IOption>)
  {
    return params?.inputValue ? opts.filter((o) => similar(o.label, params?.inputValue)) : [];
  }

  function handleAdd()
  {
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.append("protocols", (currentOption?.mmProtocolId ?
      "mmProtocolId_" + String(currentOption?.mmProtocolId) :
      "protocolId_" + String(currentOption?.protocolId)) + "_1");
    setSearchParams(newSearchParams.toString());

    setShowSearch(false);

    setCurrentOption(null);
    setOptions(options.filter(o => o.value !== currentOption?.value));
  }

  function handleRemove(value: number)
  {
    const newSearchParams = new URLSearchParams(searchParams);
    const protocolValues = searchParams
      .getAll("protocols")
      .filter(v => !v.includes(
        (value < 0 ? ("mmProtocolId_" + String(-value)) : ("protocolId_" + String(value))) +
        "_"));
    newSearchParams.delete("protocols");
    protocolValues.forEach(v => newSearchParams.append("protocols", v));
    setSearchParams(newSearchParams.toString());
  }

  function handleChangeWeighting(weighting: number, protocolOption: IProtocolOption)
  {
    const newSearchParams = new URLSearchParams(searchParams);
    const protocolValues = newSearchParams.getAll("protocols")
    newSearchParams.delete("protocols");

    const protocolId = (protocolOption.mmProtocolId ?
      "mmProtocolId_" + protocolOption.mmProtocolId:
      "protocolId_" + protocolOption.protocolId) + "_";

    protocolValues.forEach(v =>
    {
      if(v.includes(protocolId))
        v = v.split(/\d*$/)[0] + weighting;

      newSearchParams.append("protocols", v);
    });

    setSearchParams(newSearchParams.toString());
  }

  async function handleCombine()
  {
    if(!user.premium)
      return setReplaceProtocolDialog(true);

    combineAndNavigate();
  }

  async function combineAndNavigate()
  {
    const protocol = await appDispatch(combineProtocolsAsync(selectedProtocols
      .map(p => ({ ...p, ...p.protocol }))));
    if(!protocol)
      return;

    navigate(PROTOCOL + "/" + protocol.protocolId);
  }

  const ProtocolSearch = <ControlledAutocomplete
    label='Search for a protocol'
    onInputChange={handleInputChange}
    onChangeCurrentOption={handleChangeCurrentOption as (option: IOption | null) => Promise<void>}
    options={options}
    currentOption={currentOption}
    filter={optionsFilter}
    width='650px' />;

  const weightingWidth = '90px';


  return (
    <Container>
      <Stack
        justifyContent='center'
        alignItems='center'
        spacing={2}>

        <PageHeadline>Protocol Builder</PageHeadline>

        <Box sx={{ width: 650 }}>
          <Stack spacing={2} sx={{ width: '100%' }}>

              <Stack key={"header"} spacing={2} direction="row" sx={{ width: '100%' }}>
                <AddCircleIcon visibility='hidden'/>
                <Typography variant='subtitle2' sx={{ width: '100%' }}>
                  {"Protocol Name"}
                </Typography>
                <Typography variant='subtitle2' sx={{ width: weightingWidth }}>
                  {"Weighting"}
                </Typography>
                <></>
              </Stack>

            {selectedProtocols.map(p =>
              <Stack key={p.protocol.value} spacing={2} direction="row" sx={{
                width: '100%', alignItems: 'center' }}>
                <RemoveCircleIcon
                  onClick={() => handleRemove(p.protocol.value)}
                  style={{ cursor: 'pointer', color: 'red' }}/>
                <Typography variant='body2' sx={{ width: '100%' }}>
                  {p.protocol.label}
                </Typography>
                <Select
                  size='small'
                  type='text'
                  value={p.weighting}
                  sx={{ width: weightingWidth }}
                  onChange={event => handleChangeWeighting(Number(event.target.value), p.protocol)}>
                  {[{ label: "1", value: 1 }, { label: "2", value: 2 }, { label: "3", value: 3 }]
                    .map((option, index) =>
                      <MenuItem key={index} dense={true} value={option.value}>
                        {option.label}
                      </MenuItem>)}
                </Select>
              </Stack>)}

            <Stack spacing={2} direction="row" sx={{ width: '100%' }}>
              {showSearch ?
                <>
                  <AddCircleIcon visibility='hidden'/>
                  {ProtocolSearch}
                  <Button label="✔" onClick={handleAdd} />
                  <Box sx={{ width: weightingWidth }}/>
                </> :
                <AddCircleIcon
                  onClick={() => setShowSearch(true)}
                  style={{ cursor: 'pointer', color: 'green' }}/>}
            </Stack>

            <Button label="Combine" onClick={handleCombine}/>

          </Stack>
        </Box>

      </Stack>

      <ReplaceProtocolDialog
        open={replaceProtocolDialog}
        handleOk={combineAndNavigate}
        handleCancel={() => setReplaceProtocolDialog(false)}/>
    </Container>);
};

export default ProtocolBuilder;
